import { useState, useCallback } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { FiRefreshCw } from "react-icons/fi"; // Added icon for better UI

import { API_URL } from "../../config";
import { apiGET, apiPOST, apiPUT } from "../../utilities/apiHelpers";
import { setCartCount } from "../../redux/users/users";
import { getImageWithFallback } from "../../utilities/getImageWithFallback";

const OrderItemCard = ({ orderItem, globalConfig }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [orderAgainLoading, setOrderAgainLoading] = useState(false);

  const userId = useSelector((state) => state.user?.userData?.id) || "";

  // ✅ DATA EXTRACTION (Kept exactly as requested)
  const productName = orderItem?.productName || orderItem?.productDetails?.name || "Product";

  const productImage = getImageWithFallback(
    [
      orderItem?.productBannerImg,
      orderItem?.productImages?.[0],
      orderItem?.productDetails?.bannerImg,
    ],
    "product"
  );

  const itemPrice = orderItem?.price ?? orderItem?.productDetails?.price;
  const itemDiscountedPrice = orderItem?.discountedPrice ?? orderItem?.productDetails?.discountedPrice;
  const itemStock = orderItem?.productQuantity ?? orderItem?.productDetails?.productQuantity;

  /* ================= ORDER AGAIN ================= */
  const orderAgain = useCallback(async () => {
    if (!orderItem) return;

    const availableQty = itemStock || 0;
    const requestedQty = orderItem?.quantity || 0;

    if (itemStock !== undefined && availableQty < requestedQty) {
      toast.info("Product quantity is not available");
      return;
    }

    setOrderAgainLoading(true);

    try {
      const payload = {
        productId: orderItem.productId,
        userId,
        quantity: requestedQty,
      };

      const response = await apiPOST(`${API_URL}/v1/cart/add-forqtycheck`, payload);

      if (!response?.data?.status) {
        toast.error(response?.data?.data || "Failed to add product to cart");
        return;
      } 
      

      const cartResponse = await apiGET(`${API_URL}/v1/cart/all-by-user/${userId}`);
      if (cartResponse?.status) {
        dispatch(setCartCount(cartResponse.data?.data?.length || 0));
      }

      toast.success(response?.data?.data?.data || "Added to cart");

      await apiPUT(`/v1/stepper-progress/update-stepper-progress/${userId}`, {
        currentStep: 0,
      });

      navigate("/view-cart");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setOrderAgainLoading(false);
    }
  }, [orderItem, userId, dispatch, navigate, itemStock]);

  /* ================= UI ================= */
  return (
    <div
      key={orderItem?.productId}
      className="w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col sm:flex-row hover:shadow-md transition-shadow duration-300"
    >
      {/* Image Section:
         - Mobile: h-48 w-full (Banner style)
         - Desktop: w-32 h-auto (Sidebar style)
      */}
      <div className="relative w-full h-48 sm:w-40 sm:h-auto sm:shrink-0 bg-gray-50 border-b sm:border-b-0 sm:border-r border-gray-100">
        <img
          src={productImage}
          alt={productName}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = "https://placehold.co/400x400?text=Image+Error"; }}
        />
        {/* Mobile-only Qty Badge on Image */}
        <span className="sm:hidden absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded backdrop-blur-sm">
          Qty: {orderItem?.quantity}
        </span>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-4 flex flex-col justify-between min-w-0">

        {/* Top: Title & Info */}
        <div className="min-w-0">
          <div className="flex justify-between items-start gap-3">
            {/* Title with Line Clamp to prevent text cutting */}
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 line-clamp-2 leading-tight" title={productName}>
              {productName}
            </h3>

            {/* Stock Badge */}
            {itemStock !== undefined && (
              <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${itemStock > 0 ? 'bg-yellow-50 text-yellow-700 border border-yellow-100' : 'bg-red-50 text-red-600'
                }`}>
                {itemStock > 0 ? `${itemStock} left` : 'Out of Stock'}
              </span>
            )}
          </div>

          {/* Meta Data */}
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-500 font-mono">
              <span className="text-gray-400">ID:</span> {orderItem?.orderId || orderItem?._id?.slice(-6).toUpperCase()}
            </p>
            <p className="text-xs text-gray-500">
              <span className="text-gray-400">Date:</span> {moment(orderItem?.createdAt).format("DD MMM, YYYY")}
            </p>
          </div>
        </div>

        {/* Bottom: Price & Action */}
        <div className="mt-4 pt-3 border-t border-dashed border-gray-100 flex flex-wrap items-end justify-between gap-3">

          {/* Price Block */}
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total</span>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold text-teal-700">
                ₹{globalConfig?.currencyData?.symbol}
                {itemDiscountedPrice}
              </span>
              {(itemPrice > itemDiscountedPrice) && (
                <span className="text-xs text-gray-400 line-through decoration-gray-300">
                  ₹{globalConfig?.currencyData?.symbol}
                  {itemPrice}
                </span>
              )}
            </div>
          </div>

          {/* Action Block */}
          <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
            {/* Desktop Qty Display */}
            <span className="hidden sm:block text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded">
              Qty: {orderItem?.quantity}
            </span>


          </div>
          <button
            className="flex-1 sm:flex-none flex items-center justify-start gap-2  p-6 py-2 bg-teal-50 text-teal-700 hover:bg-teal-600 hover:text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 active:scale-95"
            disabled={orderAgainLoading}
            onClick={orderAgain}
          >
            {orderAgainLoading ? (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiRefreshCw size={14} />
            )}
            <span className="whitespace-nowrap">Buy Again</span>
          </button>

        </div>
      </div>
      
    </div>
    
  );
};

export default OrderItemCard;